import { useCallback } from 'react'

import {
  type InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL, IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'

import { buildPaginatedEndpoint, getNextPageParam } from '@/helpers/api'

import {
  bookmarkCheckQueryKeys,
  bookmarksQueryKeys,
} from '@/hooks/bookmarks/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type {
  Bookmark,
  BookmarkCheck,
  BookmarkableType,
} from '@/types/bookmark/Bookmark'
import type { UUID } from '@/types/Id'

interface UseBookmarksProps<TResult> {
  orgId: UUID | ''
  typeFilter?: BookmarkableType
  selector?: (
    bookmarks: InfiniteData<PaginatedResponse<Bookmark>, PaginateRequest>,
  ) => TResult
  /**
   * When true, prefills the bookmark check query cache with entries from the
   * bookmarks list. This avoids individual API calls when checking if specific
   * objects are bookmarked. Defaults to true.
   */
  prefillCheckCache?: boolean
}

export const useBookmarks = <TResult = Bookmark[]>({
  orgId,
  typeFilter,
  selector,
  prefillCheckCache = true,
}: UseBookmarksProps<TResult>) => {
  const { accessToken } = useAuthInfo()

  const queryClient = useQueryClient()

  const select = useCallback(
    (data: InfiniteData<PaginatedResponse<Bookmark>, PaginateRequest>) => {
      if (selector) {
        return selector(data)
      }
      return data.pages.map((res) => res.data).flat() as unknown as TResult
    },
    [selector],
  )

  return useInfiniteQuery({
    queryFn: async ({ pageParam }) => {
      const data = await fetchBookmarks(
        orgId,
        accessToken,
        pageParam,
        typeFilter,
      )

      // Prefill bookmark check cache for each bookmark in the response
      if (prefillCheckCache && orgId) {
        for (const bookmark of data.data) {
          const checkKey = bookmarkCheckQueryKeys.check({
            orgId,
            bookmarkableType: bookmark.bookmarkable_type,
            objectId: bookmark.object_id,
          })
          const checkData: BookmarkCheck = {
            is_bookmarked: true,
            bookmark_id: bookmark.id,
          }
          queryClient.setQueryData(checkKey, checkData)
        }
      }

      return data
    },
    queryKey: bookmarksQueryKeys.organization({ orgId, typeFilter }),
    enabled: !IS_FEDRAMP_ENVIRONMENT && !!orgId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    initialPageParam: { limit: 10000 }, // TODO
    getNextPageParam: getNextPageParam,
    select,
  })
}

export const fetchBookmarks = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
  typeFilter?: BookmarkableType,
): Promise<PaginatedResponse<Bookmark>> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    return { count: 0, has_more: false, data: [] }
  }

  let url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/bookmarks`,
    pagination,
  )

  if (typeFilter) {
    url += `&bookmarkable_type=${typeFilter}`
  }

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new Error(res.statusText)
  }

  return (await res.json()) as PaginatedResponse<Bookmark>
}
